/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { ScrollArea, sizes } from '@smartsheet/lodestar-core';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  GraphNodeTypes,
  outputSelector,
  selectedElementSelector,
} from '../../../../services/ducks/designerDuck/designerDuck';
import { selectModuleById } from '../../../../services/ducks/modulesDuck/modulesDuck';
import {
  setChildWorkflowData,
  setJunctionData,
  setModuleData,
  setStateData,
} from '../../../../services/ducks/workflowDuck/workflowDuck';
import { expandModel } from '../../../../services/forms/module/ExpandModel';
import { flattenModel } from '../../../../services/forms/module/FlattenModel';
import { ParamModel } from '../../../../services/forms/params/ParamModelTypes';
import {
  buildChildWorkflowSchema,
  buildJunctionSchema,
  buildStateSchema,
} from '../../../../services/forms/schemas/DesignerSchema';
import { DesignerForm } from '../../../forms/DesignerForm';
import { ModulePanelHeader } from './ModulePanelHeader';
import { Output } from './Output';
import { PanelHeader } from './PanelHeader';

const designerFormStyle = css`
  margin-bottom: ${sizes.large}px;
`;

export const EditModule = ({
  closePanel,
  selectedElement,
}: {
  selectedElement: ReturnType<typeof selectedElementSelector>;
  closePanel: () => void;
}) => {
  const output = useSelector(outputSelector);
  const module = useSelector(
    selectModuleById(selectedElement.moduleID, selectedElement.pluginUUID)
  );

  const dispatch = useDispatch();

  let designerForm;

  if (
    selectedElement?.type === GraphNodeTypes.MODULE ||
    selectedElement?.type === GraphNodeTypes.GROUP_JUNCTION
  ) {
    const schema = module.params;

    const flattenedModel = flattenModel(selectedElement.moduleData);

    const moduleHandler = (newModel: ParamModel) => {
      const data = expandModel(newModel, schema);
      dispatch(setModuleData({ id: selectedElement.id, moduleData: data }));
    };
    designerForm = (
      <DesignerForm
        schema={schema}
        model={flattenedModel}
        handler={moduleHandler}
      />
    );
  } else if (selectedElement?.type === GraphNodeTypes.CHILD) {
    const schema = buildChildWorkflowSchema();
    const model = selectedElement;

    const childWorkflowHandler = (data: any) => {
      dispatch(
        setChildWorkflowData({
          id: selectedElement.id,
          state: data.state,
          childConversation: data.childConversation,
          childIterator: data.childIterator,
          childInput: Array.isArray(data.childInput) ? {} : data.childInput,
        })
      );
    };

    designerForm = (
      <DesignerForm
        schema={schema}
        model={model}
        handler={childWorkflowHandler}
      />
    );
  } else if (selectedElement?.type === GraphNodeTypes.JUNCTION) {
    const schema = buildJunctionSchema();
    const model = selectedElement;

    const junctionHandler = (data: any) => {
      dispatch(
        setJunctionData({
          id: selectedElement.id,
          state: data.state,
          question: data.question,
          retryHelpTxt: data.retryHelpTxt,
        })
      );
    };

    designerForm = (
      <DesignerForm schema={schema} model={model} handler={junctionHandler} />
    );
  } else {
    const schema = buildStateSchema();
    const model = selectedElement;

    const handler = (data: any) => {
      dispatch(setStateData({ id: selectedElement.id, name: data.state }));
    };

    designerForm = (
      <DesignerForm schema={schema} model={model} handler={handler} />
    );
  }
  return (
    <React.Fragment>
      {selectedElement.type === GraphNodeTypes.MODULE ||
      selectedElement.type === GraphNodeTypes.GROUP_JUNCTION ? (
        <ModulePanelHeader
          onClose={closePanel}
          selectedElement={selectedElement}
        />
      ) : (
        <PanelHeader onClose={closePanel} selectedElement={selectedElement} />
      )}
      <ScrollArea
        gutter="none"
        scrollbarStyle="slim"
        css={css`
          margin-bottom: ${sizes.medium}px;
        `}
      >
        <div css={designerFormStyle}>{designerForm}</div>
        {output ? <Output value={output} /> : null}
      </ScrollArea>
    </React.Fragment>
  );
};
